// S6 Kanban — dynamic per-project status columns with drag-drop.
//
// Layout: one DragDropContext + one Droppable per status column (ordered by
// sortOrder from the project's statuses store).
//   - Cross-column drop → PATCH /api/tasks/:id with status_id (custom status).
//   - In-column reorder → PATCH /api/tasks/:id/reorder with new sort_order.
// Filters (Phase / Assignee / Priority) hide tasks visually but keep the
// underlying drag-indices contiguous so dnd remains predictable.
//
// Mobile: columns are 280px wide and snap-scroll horizontally (1 column
// visible at a time per UX spec S6 mobile note).

import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import type { DropResult } from "@hello-pangea/dnd";
import { Filter, Plus } from "lucide-preact";
import type {
  Phase,
  ProjectWithCounts,
  Task,
  TaskCreate,
  TaskPriority,
  TaskUpdate,
} from "@juuplan/shared";
import { TASK_PRIORITIES } from "@juuplan/shared";
import { TASK_PRIORITY_LABEL } from "../lib/format";
import { groupByStatusId } from "../lib/grouping";
import {
  createTask,
  patchTask,
  patchTaskOptimistic,
  refreshProjectDetail,
  reorderTask,
} from "../store/project";
import { matchesAllFilters, type FilterRule } from "../lib/viewState";
import { projectTaskToRecord } from "../lib/taskRecord";
import { useStatuses } from "../store/statuses";
import { useCycles } from "../store/cycles";
import { useUsers } from "../store/users";
import { toastError } from "../lib/toast";
import { FilterDisclosure } from "../components/FilterDisclosure";
import { TaskCard } from "../components/TaskCard";
import { TaskModal } from "../components/TaskModal";

interface Props {
  project: ProjectWithCounts;
  tasksByPhase: Record<number, Task[]>;
  /** Viewer-Rolle: Drag-Drop deaktiviert, TaskModal read-only. */
  readOnly?: boolean;
  /**
   * Shared view filters from Project.tsx (#872/#873). Filter SEMANTICS live once
   * in lib/viewState.ts; the local predicate below keeps only this board's own
   * concerns (phase/assignee/priority/cycle pickers).
   */
  filters?: FilterRule[];
}

/** Swimlane-Dimension (horizontale Gruppierung des Boards). */
type Swimlane = "none" | "priority" | "assignee";

interface LaneDef {
  /** priority: Prioritätswert oder "none"; assignee: "u<id>" oder "none"; none: "_all". */
  key: string;
  label: string;
}

/** droppableId einer Board-Zelle: eindeutig aus Lane + Spalte (Trenner "::"). */
function cellDroppableId(laneKey: string, colKey: string): string {
  return `kanban::${laneKey}::${colKey}`;
}

/** Zerlegt eine cellDroppableId wieder in Lane- und Spalten-Key. */
function parseCellDroppableId(
  id: string
): { laneKey: string; colKey: string } | null {
  const parts = id.split("::");
  if (parts.length !== 3 || parts[0] !== "kanban") return null;
  return { laneKey: parts[1]!, colKey: parts[2]! };
}

export function KanbanView(props: Props): preact.JSX.Element {
  const { project, tasksByPhase, readOnly = false, filters } = props;
  const users = useUsers();
  const statusesState = useStatuses(project.id);
  const cyclesState = useCycles(project.id);

  // ---- flatten + phase lookup ----
  const allTasks = useMemo(() => {
    const out: Task[] = [];
    for (const list of Object.values(tasksByPhase)) out.push(...list);
    return out;
  }, [tasksByPhase]);

  const phaseById = useMemo(() => {
    const m: Record<number, Phase> = {};
    for (const p of project.phases) m[p.id] = p;
    return m;
  }, [project.phases]);

  // ---- filters ----
  const [phaseFilter, setPhaseFilter] = useState<number | "all">("all");
  const [assigneeFilter, setAssigneeFilter] = useState<number | "all" | "unassigned">(
    "all"
  );
  const [priorityFilter, setPriorityFilter] = useState<Set<TaskPriority | "none">>(
    new Set()
  );
  /** "all" = no restriction, "none" = tasks without a cycle, number = specific cycle id. */
  const [cycleFilter, setCycleFilter] = useState<"all" | "none" | number>("all");

  const togglePriority = (p: TaskPriority | "none"): void => {
    setPriorityFilter((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const matchesFilter = (t: Task): boolean => {
    // Shared, cross-view filters first (type, hide-done, status, priority, …).
    if (filters && filters.length > 0 && !matchesAllFilters(projectTaskToRecord(t), filters)) {
      return false;
    }
    if (phaseFilter !== "all" && t.phase_id !== phaseFilter) return false;
    if (assigneeFilter === "unassigned" && t.assignee_ids.length > 0) return false;
    if (
      typeof assigneeFilter === "number" &&
      !t.assignee_ids.includes(assigneeFilter)
    ) {
      return false;
    }
    if (priorityFilter.size > 0) {
      const key: TaskPriority | "none" = t.priority ?? "none";
      if (!priorityFilter.has(key)) return false;
    }
    if (cycleFilter === "none" && t.cycle_id !== null) return false;
    if (typeof cycleFilter === "number" && t.cycle_id !== cycleFilter) return false;
    return true;
  };

  const hasActiveFilters =
    phaseFilter !== "all" ||
    assigneeFilter !== "all" ||
    priorityFilter.size > 0 ||
    cycleFilter !== "all";

  // ---- group by dynamic status id ----
  const columns = useMemo(
    () => groupByStatusId(allTasks, statusesState.items),
    [allTasks, statusesState.items]
  );

  // ---- swimlanes (horizontale Gruppierung nach Priorität / Zuständig) ----
  // Persistiert pro Projekt in localStorage. Phase ist bewusst KEINE Option:
  // Phasenwechsel laufen über den Reorder-Pfad, nicht über patchTask — und
  // Priorität/Zuständig lassen sich beim Ziehen zwischen Lanes sauber setzen.
  const swimlaneKey = `kanban-swimlane:${project.id}`;
  const [swimlane, setSwimlaneState] = useState<Swimlane>(() => {
    try {
      const v = localStorage.getItem(swimlaneKey);
      return v === "priority" || v === "assignee" ? v : "none";
    } catch {
      return "none";
    }
  });
  const setSwimlane = (v: Swimlane): void => {
    setSwimlaneState(v);
    try {
      localStorage.setItem(swimlaneKey, v);
    } catch {
      /* localStorage kann fehlschlagen (Private Mode) — nicht kritisch */
    }
  };

  // Lane-Definitionen für die aktive Dimension.
  const lanes = useMemo<LaneDef[]>(() => {
    if (swimlane === "priority") {
      const order: (TaskPriority | "none")[] = [
        "urgent",
        "high",
        "medium",
        "low",
        "none",
      ];
      return order.map((p) => ({
        key: p,
        label: p === "none" ? "Ohne Priorität" : TASK_PRIORITY_LABEL[p],
      }));
    }
    if (swimlane === "assignee") {
      // Nur PRIMÄRE Zuständige (jede Aufgabe genau einer Lane → eindeutige
      // draggableIds), + eine Lane für nicht zugewiesene Aufgaben (falls
      // vorhanden). Ziehen zwischen Lanes setzt den Zuständigen entsprechend.
      const ids = new Set<number>();
      let hasUnassigned = false;
      for (const t of allTasks) {
        if (t.primary_assignee_id === null) hasUnassigned = true;
        else ids.add(t.primary_assignee_id);
      }
      const userLanes: LaneDef[] = [...ids].map((uid) => ({
        key: `u${uid}`,
        label: users.items.find((u) => u.id === uid)?.name ?? `User #${uid}`,
      }));
      userLanes.sort((a, b) => a.label.localeCompare(b.label));
      if (hasUnassigned) userLanes.push({ key: "none", label: "Nicht zugewiesen" });
      return userLanes;
    }
    return [{ key: "_all", label: "" }];
  }, [swimlane, allTasks, users.items]);

  /** Gehört eine Aufgabe in die Lane mit diesem Key? */
  const inLane = (t: Task, laneKey: string): boolean => {
    if (swimlane === "none") return true;
    if (swimlane === "priority") return (t.priority ?? "none") === laneKey;
    // assignee — nach primärem Zuständigen (eindeutige Lane pro Aufgabe)
    if (laneKey === "none") return t.primary_assignee_id === null;
    return t.primary_assignee_id === Number(laneKey.slice(1));
  };

  // ---- modal ----
  const [openTaskId, setOpenTaskId] = useState<number | null>(null);
  const openTask = allTasks.find((t) => t.id === openTaskId) ?? null;

  // Deep-link open: ?task=<id> in the URL opens the matching task modal
  // immediately (mirrors TableView's handling for the same query param).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("task");
    if (!raw) return;
    const id = Number.parseInt(raw, 10);
    if (Number.isNaN(id)) return;
    setOpenTaskId(id);
    params.delete("task");
    const query = params.toString();
    const next =
      window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
    window.history.replaceState({}, "", next);
  }, [project.id]);

  // ---- drag/drop ----
  const onDragEnd = async (result: DropResult): Promise<void> => {
    if (!result.destination) return;
    const { source, destination, draggableId } = result;

    const taskId = Number(draggableId.replace("kanban-task-", ""));
    if (!Number.isFinite(taskId)) return;

    const src = parseCellDroppableId(source.droppableId);
    const dst = parseCellDroppableId(destination.droppableId);
    if (!src || !dst) return;

    try {
      const patch: TaskUpdate = {};

      // Spaltenwechsel → Statuswechsel. In die "_fallback"-Spalte darf der
      // Status nicht gesetzt werden (dort gibt es keinen Status).
      if (src.colKey !== dst.colKey) {
        const toStatusId = dst.colKey.startsWith("status-id:")
          ? Number(dst.colKey.replace("status-id:", ""))
          : null;
        if (toStatusId === null) return;
        patch.status_id = toStatusId;
      }

      // Lane-Wechsel → Prioritäts- bzw. Zuständigkeits-Änderung.
      if (swimlane !== "none" && src.laneKey !== dst.laneKey) {
        if (swimlane === "priority") {
          patch.priority = dst.laneKey === "none" ? null : (dst.laneKey as TaskPriority);
        } else if (swimlane === "assignee") {
          patch.assignee_ids =
            dst.laneKey === "none" ? [] : [Number(dst.laneKey.slice(1))];
        }
      }

      if (Object.keys(patch).length > 0) {
        await patchTask(taskId, patch);
        return;
      }

      // Kein Status-/Lane-Wechsel: nur In-Spalten-Reorder — ausschließlich im
      // Flach-Modus ohne aktive Filter (sonst verfälschen versteckte Karten den
      // Zielindex, und pro-Lane-Reihenfolge ist bei globalem sort_order nicht
      // eindeutig).
      if (
        swimlane === "none" &&
        !hasActiveFilters &&
        source.index !== destination.index
      ) {
        await reorderTask(taskId, { sort_order: destination.index });
      }
    } catch {
      void refreshProjectDetail();
    }
  };

  // Drag is enabled whenever the board is editable. With a filter active we
  // still allow moving a card to another status column — that's a pure status
  // change and index-independent — but in-column reordering is skipped in
  // onDragEnd because hidden (filtered-out) cards skew the drop index.
  const dragDisabled = readOnly;

  // ---- inline card creation ----
  // Target phase for new cards: the active phase filter when one is set,
  // otherwise the project's first phase. Tasks require a phase (columns are
  // status-based), so without any phase we can't offer the add affordance.
  const targetPhaseId: number | null =
    phaseFilter !== "all"
      ? phaseFilter
      : (project.phases[0]?.id ?? null);
  const targetPhaseName =
    targetPhaseId !== null ? (phaseById[targetPhaseId]?.name ?? null) : null;

  const handleAdd = async (
    columnKey: string,
    laneKey: string,
    title: string
  ): Promise<void> => {
    const trimmed = title.trim();
    if (!trimmed || targetPhaseId === null) return;
    // Column key "status-id:<n>" → set that status after create; the create
    // endpoint has no status field, so a new task always lands in the default
    // backlog status and is then moved into its column.
    const statusId = columnKey.startsWith("status-id:")
      ? Number(columnKey.replace("status-id:", ""))
      : null;
    // In einer Swimlane wird das Lane-Attribut gleich mit angelegt, damit die
    // neue Karte in genau dieser Lane landet.
    const body: TaskCreate = { title: trimmed };
    if (swimlane === "priority" && laneKey !== "none") {
      body.priority = laneKey as TaskPriority;
    } else if (swimlane === "assignee" && laneKey !== "none" && laneKey !== "_all") {
      body.assignee_ids = [Number(laneKey.slice(1))];
    }
    try {
      const task = await createTask(targetPhaseId, body);
      if (statusId !== null) {
        await patchTaskOptimistic(task.id, { status_id: statusId });
      }
    } catch {
      toastError("Aufgabe konnte nicht erstellt werden.");
    }
  };

  return (
    <div class="flex flex-col gap-3">
      {/* Filter bar */}
      <div
        class="flex flex-wrap items-center gap-2 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2"
        role="group"
        aria-label="Kanban-Filter"
      >
        <span class="hidden sm:inline-flex items-center gap-1 text-label uppercase tracking-wider text-[color:var(--color-on-surface-variant)] font-semibold mr-1">
          <Filter size={14} aria-hidden="true" />
          Filter
        </span>

        <FilterDisclosure
          count={
            (phaseFilter !== "all" ? 1 : 0) +
            (assigneeFilter !== "all" ? 1 : 0) +
            (cycleFilter !== "all" ? 1 : 0) +
            (priorityFilter.size > 0 ? 1 : 0)
          }
        >
        {/* Phase */}
        <label class="inline-flex items-center gap-1.5 text-xs text-[color:var(--color-on-surface-variant)]">
          <span class="sr-only">Phase</span>
          <select
            value={phaseFilter === "all" ? "all" : String(phaseFilter)}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              setPhaseFilter(v === "all" ? "all" : Number(v));
            }}
            class="w-full sm:w-auto rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 min-h-[36px] sm:min-h-[28px]"
            aria-label="Phase filtern"
          >
            <option value="all">Alle Phasen</option>
            {project.phases.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </select>
        </label>

        {/* Assignee */}
        <label class="inline-flex items-center gap-1.5 text-xs text-[color:var(--color-on-surface-variant)]">
          <span class="sr-only">Zuständig</span>
          <select
            value={
              assigneeFilter === "all"
                ? "all"
                : assigneeFilter === "unassigned"
                  ? "unassigned"
                  : String(assigneeFilter)
            }
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              if (v === "all") setAssigneeFilter("all");
              else if (v === "unassigned") setAssigneeFilter("unassigned");
              else setAssigneeFilter(Number(v));
            }}
            class="w-full sm:w-auto rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 min-h-[36px] sm:min-h-[28px]"
            aria-label="Zuständig filtern"
          >
            <option value="all">Alle Zuständigen</option>
            <option value="unassigned">— Nicht zugewiesen —</option>
            {/* Native <option> erlaubt kein Badge — Bots werden im Text
                gekennzeichnet. */}
            {users.items.map((u) => (
              <option key={u.id} value={String(u.id)}>
                {u.name}
                {u.is_bot ? " (Bot)" : ""}
              </option>
            ))}
          </select>
        </label>

        {/* Zyklus */}
        {!cyclesState.loading && cyclesState.items.length > 0 && (
          <label class="inline-flex items-center gap-1.5 text-xs text-[color:var(--color-on-surface-variant)]">
            <span class="sr-only">Zyklus</span>
            <select
              value={cycleFilter === "all" ? "all" : cycleFilter === "none" ? "none" : String(cycleFilter)}
              onChange={(e) => {
                const v = (e.target as HTMLSelectElement).value;
                if (v === "all") setCycleFilter("all");
                else if (v === "none") setCycleFilter("none");
                else setCycleFilter(Number(v));
              }}
              class="w-full sm:w-auto rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 min-h-[36px] sm:min-h-[28px]"
              aria-label="Zyklus filtern"
            >
              <option value="all">Alle Zyklen</option>
              <option value="none">Kein Zyklus</option>
              {cyclesState.items.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Priority multi-select chips */}
        <div class="inline-flex items-center gap-1" role="group" aria-label="Priorität filtern">
          {(TASK_PRIORITIES as readonly TaskPriority[]).map((p) => {
            const active = priorityFilter.has(p);
            return (
              <button
                key={p}
                type="button"
                onClick={() => togglePriority(p)}
                class={`rounded-full border px-2 py-0.5 text-label font-medium min-h-[24px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 ${
                  active
                    ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary-strong)]"
                    : "border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                }`}
                aria-pressed={active}
              >
                {TASK_PRIORITY_LABEL[p]}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => togglePriority("none")}
            class={`rounded-full border px-2 py-0.5 text-label font-medium min-h-[24px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 ${
              priorityFilter.has("none")
                ? "border-[color:var(--color-primary)] bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary-strong)]"
                : "border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)]"
            }`}
            aria-pressed={priorityFilter.has("none")}
            title="Aufgaben ohne Priorität"
          >
            Ohne
          </button>
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setPhaseFilter("all");
              setAssigneeFilter("all");
              setPriorityFilter(new Set());
              setCycleFilter("all");
            }}
            class="ml-auto rounded px-2 py-1 text-label text-[color:var(--color-primary-strong)] hover:bg-[color:var(--color-surface-container-low)] transition-colors"
          >
            Filter zurücksetzen
          </button>
        )}
        {hasActiveFilters && (
          <span class="text-label text-[color:var(--color-on-surface-variant)] italic">
            Statuswechsel möglich · Sortieren pausiert solange Filter aktiv
          </span>
        )}
        </FilterDisclosure>

        {/* Swimlane-Auswahl */}
        <label class="ml-auto inline-flex items-center gap-1.5 text-xs text-[color:var(--color-on-surface-variant)]">
          <span class="hidden sm:inline">Swimlanes</span>
          <select
            value={swimlane}
            onChange={(e) =>
              setSwimlane((e.target as HTMLSelectElement).value as Swimlane)
            }
            class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base sm:text-xs text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1 min-h-[36px] sm:min-h-[28px]"
            aria-label="Swimlanes (Zeilen-Gruppierung)"
          >
            <option value="none">Keine</option>
            <option value="priority">Nach Priorität</option>
            <option value="assignee">Nach Zuständig</option>
          </select>
        </label>
      </div>

      {/* Columns (ggf. in Swimlane-Bändern gruppiert) */}
      <DragDropContext onDragEnd={(r) => void onDragEnd(r)}>
        {statusesState.loading ? (
          <p class="text-sm text-[color:var(--color-on-surface-variant)] italic px-2 py-4">
            Statuses werden geladen…
          </p>
        ) : columns.length === 0 ? (
          <div class="w-full rounded border border-dashed border-[color:var(--color-outline-variant)] px-4 py-10 text-center">
            <p class="text-sm font-medium text-[color:var(--color-on-surface)]">
              Noch keine Spalten
            </p>
            <p class="mt-1 text-xs text-[color:var(--color-on-surface-variant)]">
              Lege in den Projekt-Einstellungen Statuses an, um das Board zu nutzen.
            </p>
          </div>
        ) : (
          <div class="flex flex-col gap-4">
            {lanes.map((lane) => {
              // Karten dieser Lane (Gesamtzahl der Lane, unabhängig vom Filter).
              const laneTasks =
                swimlane === "none"
                  ? null
                  : allTasks.filter((t) => inLane(t, lane.key));
              return (
                <div key={lane.key} class="flex flex-col gap-2">
                  {swimlane !== "none" && (
                    <div class="flex items-center gap-2 px-1">
                      <h4 class="text-xs font-semibold text-[color:var(--color-on-surface)]">
                        {lane.label}
                      </h4>
                      <span class="inline-flex items-center justify-center rounded-full bg-[color:var(--color-surface-container-high)] px-2 py-0.5 text-label font-semibold tabular-nums text-[color:var(--color-on-surface-variant)]">
                        {laneTasks?.length ?? 0}
                      </span>
                    </div>
                  )}
                  <div
                    class="flex gap-3 overflow-x-auto snap-x snap-mandatory md:snap-none pb-2 -mx-1 px-1"
                    role="list"
                    aria-label={
                      swimlane === "none"
                        ? "Kanban-Spalten"
                        : `Kanban-Spalten — ${lane.label}`
                    }
                  >
                    {columns.map((col) => {
                      const cellTasks =
                        swimlane === "none"
                          ? col.tasks
                          : col.tasks.filter((t) => inLane(t, lane.key));
                      const visibleList = cellTasks.filter(matchesFilter);
                      return (
                        <KanbanColumn
                          key={`${lane.key}:${col.key}`}
                          droppableId={cellDroppableId(lane.key, col.key)}
                          label={col.label}
                          color={col.color}
                          wipLimit={col.wipLimit ?? null}
                          showWip={swimlane === "none"}
                          tasks={cellTasks}
                          visibleCount={visibleList.length}
                          phaseById={phaseById}
                          projectColor={project.color}
                          matchesFilter={matchesFilter}
                          dragDisabled={dragDisabled}
                          onOpen={(id) => setOpenTaskId(id)}
                          canAdd={
                            !readOnly &&
                            targetPhaseId !== null &&
                            col.key.startsWith("status-id:")
                          }
                          addPhaseName={targetPhaseName}
                          onAdd={(title) => handleAdd(col.key, lane.key, title)}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DragDropContext>

      {openTask && (
        <TaskModal
          task={openTask}
          siblings={allTasks}
          projectId={project.id}
          phases={project.phases}
          onClose={() => setOpenTaskId(null)}
          onDeleted={() => setOpenTaskId(null)}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

// ---------- column ----------

interface ColProps {
  /** Eindeutige droppableId dieser Zelle (Lane + Spalte). */
  droppableId: string;
  label: string;
  color: string;
  /** WIP-Limit dieser Spalte; null = kein Limit. */
  wipLimit: number | null;
  /** WIP-Badge/-Färbung anzeigen (nur im Flach-Modus sinnvoll). */
  showWip: boolean;
  tasks: Task[];
  visibleCount: number;
  phaseById: Record<number, Phase>;
  projectColor: string | null;
  matchesFilter: (t: Task) => boolean;
  dragDisabled: boolean;
  onOpen: (id: number) => void;
  /** Whether the inline "add card" affordance is shown for this column. */
  canAdd: boolean;
  /** Name of the phase new cards land in — shown as a hint. */
  addPhaseName: string | null;
  /** Create a task with this column's status. Returns when the POST settled. */
  onAdd: (title: string) => Promise<void>;
}

function KanbanColumn(props: ColProps): preact.JSX.Element {
  const {
    droppableId,
    label,
    color,
    wipLimit,
    showWip,
    tasks,
    visibleCount,
    phaseById,
    projectColor,
    matchesFilter,
    dragDisabled,
    onOpen,
    canAdd,
    addPhaseName,
    onAdd,
  } = props;

  // Inline add: collapsed to a button until the user opens the input.
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const submitAdd = async (): Promise<void> => {
    const trimmed = draft.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onAdd(trimmed);
      setDraft("");
      // Keep the input open + focused for rapid multi-entry.
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const closeAdd = (): void => {
    setAdding(false);
    setDraft("");
  };

  return (
    <section
      class="flex w-[280px] md:w-[260px] lg:w-[280px] flex-shrink-0 snap-start flex-col rounded-md border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-low)]"
      aria-label={`Spalte ${label}`}
      role="listitem"
    >
      {(() => {
        // WIP-Limit bezieht sich auf die tatsächliche Kartenzahl der Spalte
        // (nicht auf die gefilterte Sicht). Bei Überschreitung: rotes Badge +
        // roter Spaltenkopf.
        const overLimit = showWip && wipLimit !== null && tasks.length > wipLimit;
        const showLimit = showWip && wipLimit !== null;
        return (
          <header
            class={`flex items-center justify-between gap-2 rounded-t-md px-3 py-2.5 border-b ${
              overLimit
                ? "border-[color:var(--color-error)] bg-[color:var(--color-error-container,#FEE2E2)]/40"
                : "border-[color:var(--color-outline-variant)]"
            }`}
          >
            <h3 class="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface)] min-w-0">
              <span
                class="inline-block size-2 rounded-full shrink-0"
                style={{ background: color }}
                aria-hidden="true"
              />
              <span class="truncate">{label}</span>
            </h3>
            <span
              class={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-label font-semibold tabular-nums shrink-0 ${
                overLimit
                  ? "bg-[color:var(--color-error)] text-[color:var(--color-on-error,#fff)]"
                  : "bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]"
              }`}
              title={
                showLimit
                  ? `${tasks.length} von max. ${wipLimit} (WIP-Limit)`
                  : undefined
              }
            >
              {showLimit
                ? `${visibleCount === tasks.length ? tasks.length : `${visibleCount}/${tasks.length}`} / ${wipLimit}`
                : visibleCount === tasks.length
                  ? tasks.length
                  : `${visibleCount}/${tasks.length}`}
            </span>
          </header>
        );
      })()}

      <Droppable droppableId={droppableId} type="KANBAN_TASK">
        {(prov, snap) => (
          <div
            ref={prov.innerRef}
            {...prov.droppableProps}
            class={`flex flex-col gap-2 p-2 min-h-[120px] flex-1 transition-colors ${
              snap.isDraggingOver
                ? "bg-[color:var(--color-surface-container)]"
                : ""
            }`}
          >
            {tasks.length === 0 ? (
              <p class="px-1 py-6 text-center text-micro text-[color:var(--color-on-surface-variant)] italic">
                Keine Aufgaben
              </p>
            ) : (
              tasks.map((task, idx) => {
                const phase = phaseById[task.phase_id] ?? null;
                const visible = matchesFilter(task);
                return (
                  <Draggable
                    key={task.id}
                    draggableId={`kanban-task-${task.id}`}
                    index={idx}
                    isDragDisabled={dragDisabled}
                  >
                    {(dragProv, dragSnap) => {
                      const baseStyle =
                        (dragProv.draggableProps.style as
                          | preact.JSX.CSSProperties
                          | undefined) ?? {};
                      return (
                        <TaskCard
                          task={task}
                          phase={phase}
                          projectColor={projectColor}
                          onOpen={() => onOpen(task.id)}
                          innerRef={dragProv.innerRef as (el: HTMLElement | null) => void}
                          draggableProps={
                            dragProv.draggableProps as unknown as Record<
                              string,
                              unknown
                            >
                          }
                          dragHandleProps={
                            (dragProv.dragHandleProps ?? undefined) as
                              | Record<string, unknown>
                              | undefined
                          }
                          isDragging={dragSnap.isDragging}
                          showDragHandle={!dragDisabled}
                          style={{
                            ...baseStyle,
                            display: visible ? baseStyle.display : "none",
                          }}
                        />
                      );
                    }}
                  </Draggable>
                );
              })
            )}
            {prov.placeholder}
            {tasks.length > 0 && visibleCount === 0 && (
              <p class="px-1 py-3 text-center text-micro text-[color:var(--color-on-surface-variant)] italic">
                Keine Aufgaben passen zum Filter
              </p>
            )}
          </div>
        )}
      </Droppable>

      {/* Inline "add card" footer — status implied by this column. */}
      {canAdd && (
        <div class="border-t border-[color:var(--color-outline-variant)] p-2">
          {!adding ? (
            <button
              type="button"
              onClick={() => setAdding(true)}
              class="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-xs font-medium text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container)] hover:text-[color:var(--color-on-surface)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1"
            >
              <Plus size={14} aria-hidden="true" />
              Aufgabe hinzufügen
            </button>
          ) : (
            <div class="flex flex-col gap-1.5">
              <input
                ref={inputRef}
                type="text"
                value={draft}
                autoFocus
                disabled={busy}
                onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void submitAdd();
                  } else if (e.key === "Escape") {
                    e.preventDefault();
                    closeAdd();
                  }
                }}
                placeholder="Titel der Aufgabe …"
                aria-label={`Aufgabe in Spalte ${label} hinzufügen`}
                maxLength={200}
                class="w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1.5 text-xs text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:opacity-60"
              />
              <div class="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => void submitAdd()}
                  disabled={busy || !draft.trim()}
                  class="rounded bg-[color:var(--color-primary)] px-2.5 py-1 text-micro font-medium text-[color:var(--color-on-primary)] hover:opacity-90 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-1"
                >
                  {busy ? "Anlegen …" : "Anlegen"}
                </button>
                <button
                  type="button"
                  onClick={closeAdd}
                  class="text-micro text-[color:var(--color-on-surface-variant)] hover:underline focus:outline-none"
                >
                  Abbrechen
                </button>
                {addPhaseName && (
                  <span
                    class="ml-auto truncate text-label text-[color:var(--color-on-surface-variant)]/70"
                    title={`Neue Aufgabe wird in Phase „${addPhaseName}" angelegt`}
                  >
                    → {addPhaseName}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
